import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  InputGroup,
  Nav,
  Tab,
  Badge,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { reportService, excavatorService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import moment from "moment";
import SignatureCanvas from "react-signature-canvas";

const CreateReport = () => {
  const { excavatorId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isManager } = useContext(AuthContext);
  const { t } = useLanguage();
  const { mode } = useTheme();
  const [excavators, setExcavators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("excavator");

  // Form state
  const [formData, setFormData] = useState({
    excavatorId: excavatorId || "",
    type: "routine",
    serviceDate: "", // Initialize as empty instead of current date
    equipmentHours: "",
    location: "",
    technician: currentUser?.name || "",
    status: "pending",
    model: "",
    serialNumber: "",
    year: "",
    machineType: "",
    size: "",
    fuelLevel: "",
    operatingHours: "",
    nextMaintenance: "",
    nextInspection: "",
    exterior: {
      chassis: false,
      boom: false,
      bucket: false,
      hydraulics: false,
      mirrors: false,
      windows: false,
      cameras: false,
      lights: false,
      wipers: false,
      lubricationSystem: false,
    },
    interior: {
      seat: false,
      electronics: false,
      heating: false,
      airConditioning: false,
      joystick: false,
      display: false,
      controls: false,
      parkingBrake: false,
    },
    engine: {
      engineOil: false,
      hydraulicOil: false,
      coolant: false,
      windshieldWasher: false,
      transmissionOil: false,
      fuelFilter: false,
      airFilter: false,
    },
    cleaning: {
      washed: false,
      windowsCleaned: false,
      mirrorsCleaned: false,
      interiorWindowsCleaned: false,
      interiorWiped: false,
      interiorVacuumed: false,
    },
    notes: "",
    reviewedBy: "",
    technicianSignature: null,
    managerSignature: null,
  });

  // Signature pads
  const techSignatureRef = useRef(null);
  const managerSignatureRef = useRef(null);

  useEffect(() => {
    fetchExcavators();
    // Use the current user's signature as the technician signature if available
    if (currentUser && currentUser.signature) {
      setFormData((prev) => ({
        ...prev,
        technicianSignature: currentUser.signature,
      }));
    }
  }, [currentUser]);

  const fetchExcavators = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the excavator service to fetch excavators
      const response = await excavatorService.getAll();
      const excavatorData = response.data.data || response.data;
      setExcavators(Array.isArray(excavatorData) ? excavatorData : []);

      // If an excavatorId was provided in URL and it exists in the fetched excavators
      if (excavatorId) {
        try {
          // Get specific excavator details if ID is provided
          const excavatorResponse = await excavatorService.getById(excavatorId);
          const selectedExcavator = excavatorResponse.data;

          if (selectedExcavator) {
            populateMachineData(selectedExcavator);
          }
        } catch (err) {
          console.error("Error fetching specific excavator:", err);
        }
      }
    } catch (err) {
      console.error("Error fetching excavators:", err);
      setError("Failed to load excavators. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Function to populate machine data from selected excavator
  const populateMachineData = (excavator) => {
    setFormData((prev) => ({
      ...prev,
      excavatorId: excavator._id,
      model: excavator.model || "",
      serialNumber: excavator.serialNumber || "",
      year: excavator.year || "",
      location: excavator.location || prev.location,
      machineType: excavator.machineType || "",
      size: excavator.size || "",
      operatingHours: excavator.hours || "",
      equipmentHours: excavator.hours || "",
      nextMaintenance: excavator.nextMaintenanceHours || "",
      nextInspection: excavator.nextInspectionDate
        ? moment(excavator.nextInspectionDate).format("YYYY-MM-DD")
        : "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for excavatorId to auto-populate machine information
    if (name === "excavatorId" && value) {
      const selectedExcavator = excavators.find((ex) => ex._id === value);
      if (selectedExcavator) {
        populateMachineData(selectedExcavator);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCheckboxChange = (section, field, checked) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: checked,
      },
    }));
  };

  const handleTechSignature = () => {
    if (techSignatureRef.current && !techSignatureRef.current.isEmpty()) {
      const signatureDataURL = techSignatureRef.current.toDataURL();
      setFormData({ ...formData, technicianSignature: signatureDataURL });
    }
  };

  const clearTechSignature = () => {
    if (techSignatureRef.current) {
      techSignatureRef.current.clear();
      setFormData({ ...formData, technicianSignature: null });
    }
  };

  // Manager signature is only needed for managers/admins or for completed reports
  const handleManagerSignature = () => {
    if (managerSignatureRef.current && !managerSignatureRef.current.isEmpty()) {
      const signatureDataURL = managerSignatureRef.current.toDataURL();
      setFormData({ ...formData, managerSignature: signatureDataURL });
    }
  };

  const clearManagerSignature = () => {
    if (managerSignatureRef.current) {
      managerSignatureRef.current.clear();
      setFormData({ ...formData, managerSignature: null });
    }
  };

  const validateForm = () => {
    // Basic validation
    if (!formData.excavatorId) {
      setError("Please select an excavator.");
      return false;
    }

    // Add validation for serviceDate
    if (!formData.serviceDate) {
      setError("Please select a service date.");
      return false;
    }

    // Check that operational hours are entered
    if (!formData.operatingHours && !formData.equipmentHours) {
      setError("Please enter the current operating hours.");
      return false;
    }

    // Technician signature is required
    if (!formData.technicianSignature) {
      setError("Technician signature is required to submit this report.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      // Debug current user information
      console.log("Current user data:", currentUser);

      // If technician has signed but no manager signature, report is pending approval
      // If both signatures exist, report is fully signed
      const reportStatus = formData.managerSignature ? "signed" : "unsigned";

      // Check if user is logged in and has an ID
      if (!currentUser) {
        setError("You must be logged in to submit a report.");
        setSubmitting(false);
        return;
      }

      // Get user ID from currentUser object - handle different possible structures
      const operatorId = currentUser._id || currentUser.id || null;

      if (!operatorId) {
        console.error("User is logged in but no ID found in:", currentUser);
        setError(
          "Could not determine your user ID. Please try logging out and back in."
        );
        setSubmitting(false);
        return;
      }

      // Format the data to match what the backend expects
      const reportData = {
        excavatorId: formData.excavatorId,
        operatorId: operatorId,
        maintenanceDate: formData.serviceDate, // Use the selected date
        fuelLevel: formData.fuelLevel,
        operatingHours: formData.operatingHours,
        nextMaintenance: formData.nextMaintenance,
        uvvInspectionDate: formData.nextInspection,
        checks: {
          exterior: Object.keys(formData.exterior).map((key) => ({
            name: key,
            status: formData.exterior[key] ? "completed" : "not done",
          })),
          interior: Object.keys(formData.interior).map((key) => ({
            name: key,
            status: formData.interior[key] ? "completed" : "not done",
          })),
          engine: Object.keys(formData.engine).map((key) => ({
            name: key,
            status: formData.engine[key] ? "completed" : "not done",
          })),
          cleaning: Object.keys(formData.cleaning).map((key) => ({
            name: key,
            status: formData.cleaning[key] ? "completed" : "not done",
          })),
        },
        notes: formData.notes,
        operatorSignature: formData.technicianSignature,
        managerSignature: formData.managerSignature || null,
      };

      console.log("Sending report data:", reportData);
      const response = await reportService.create(reportData);
      setSuccess(true);

      // Redirect to the report detail page
      setTimeout(() => {
        navigate(`/reports/${response.data.report._id}`);
      }, 1500);
    } catch (err) {
      console.error("Error submitting report:", err);
      setError(
        `Failed to submit maintenance report: ${
          err.response?.data?.message || "Please try again."
        }`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate completion percentage for each section
  const getCompletionPercentage = (section) => {
    const totalItems = Object.keys(formData[section]).length;
    const completedItems = Object.values(formData[section]).filter(
      Boolean
    ).length;
    return Math.round((completedItems / totalItems) * 100);
  };

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
        <p className="mt-2">{t("loading")}...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col lg={12} className="mb-4">
            <div
              className={`d-flex justify-content-between align-items-center p-3 rounded ${
                mode === "dark" ? "bg-dark text-white" : "bg-light"
              }`}
            >
              <div>
                <h4 className="mb-0">{t("createMaintenanceReport")}</h4>
                <p className="text-muted mb-0">
                  {moment().format("MMMM DD, YYYY")}
                </p>
              </div>
              <div>
                <Button
                  variant="secondary"
                  className="me-2"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                >
                  {t("cancel")}
                </Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {t("submitting")}
                    </>
                  ) : (
                    t("submitReport")
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-4">
            {t("reportSubmittedSuccessfully")}
          </Alert>
        )}

        <Row className="mb-4">
          {/* Left Panel - Navigation */}
          <Col lg={3}>
            <div
              className={`position-sticky ${
                mode === "dark" ? "bg-dark" : "bg-white"
              }`}
              style={{ top: "1rem" }}
            >
              <Card
                className={`shadow-sm ${
                  mode === "dark" ? "bg-dark text-white border-dark" : ""
                }`}
              >
                <Card.Body className="p-0">
                  <Nav
                    variant="pills"
                    className="flex-column"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                  >
                    <Nav.Item>
                      <Nav.Link
                        eventKey="excavator"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-truck me-2"></i> {t("excavator")}
                        </span>
                        {formData.excavatorId && (
                          <i className="bi bi-check-circle text-success"></i>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="service"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-wrench me-2"></i>{" "}
                          {t("serviceInformation")}
                        </span>
                        {formData.operatingHours && (
                          <i className="bi bi-check-circle text-success"></i>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="exterior"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-card-checklist me-2"></i>{" "}
                          {t("exteriorChecks")}
                        </span>
                        <Badge
                          bg={
                            getCompletionPercentage("exterior") === 100
                              ? "success"
                              : getCompletionPercentage("exterior") > 0
                              ? "warning"
                              : "secondary"
                          }
                          pill
                        >
                          {getCompletionPercentage("exterior")}%
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="interior"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-card-checklist me-2"></i>{" "}
                          {t("interiorChecks")}
                        </span>
                        <Badge
                          bg={
                            getCompletionPercentage("interior") === 100
                              ? "success"
                              : getCompletionPercentage("interior") > 0
                              ? "warning"
                              : "secondary"
                          }
                          pill
                        >
                          {getCompletionPercentage("interior")}%
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="engine"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-card-checklist me-2"></i>{" "}
                          {t("engineChecks")}
                        </span>
                        <Badge
                          bg={
                            getCompletionPercentage("engine") === 100
                              ? "success"
                              : getCompletionPercentage("engine") > 0
                              ? "warning"
                              : "secondary"
                          }
                          pill
                        >
                          {getCompletionPercentage("engine")}%
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="cleaning"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-card-checklist me-2"></i>{" "}
                          {t("cleaning")}
                        </span>
                        <Badge
                          bg={
                            getCompletionPercentage("cleaning") === 100
                              ? "success"
                              : getCompletionPercentage("cleaning") > 0
                              ? "warning"
                              : "secondary"
                          }
                          pill
                        >
                          {getCompletionPercentage("cleaning")}%
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="notes"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-journal-text me-2"></i>{" "}
                          {t("notes")}
                        </span>
                        {formData.notes && (
                          <i className="bi bi-check-circle text-success"></i>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                        eventKey="signatures"
                        className={`d-flex justify-content-between align-items-center ${
                          mode === "dark" ? "text-light" : ""
                        }`}
                      >
                        <span>
                          <i className="bi bi-pen me-2"></i> {t("signatures")}
                        </span>
                        {formData.technicianSignature && (
                          <i className="bi bi-check-circle text-success"></i>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Body>
              </Card>

              <Card
                className={`mt-4 shadow-sm ${
                  mode === "dark" ? "bg-dark text-white border-dark" : ""
                }`}
              >
                <Card.Body>
                  <h5 className="mb-3">{t("completionStatus")}</h5>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t("excavator")}</span>
                      <span>{formData.excavatorId ? "100%" : "0%"}</span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar ${
                          formData.excavatorId ? "bg-success" : "bg-secondary"
                        }`}
                        role="progressbar"
                        style={{ width: formData.excavatorId ? "100%" : "0%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t("serviceInformation")}</span>
                      <span>{formData.operatingHours ? "100%" : "0%"}</span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar ${
                          formData.operatingHours
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                        role="progressbar"
                        style={{
                          width: formData.operatingHours ? "100%" : "0%",
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t("checksCompleted")}</span>
                      <span>
                        {Math.round(
                          (getCompletionPercentage("exterior") +
                            getCompletionPercentage("interior") +
                            getCompletionPercentage("engine") +
                            getCompletionPercentage("cleaning")) /
                            4
                        )}
                        %
                      </span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className="progress-bar bg-success"
                        role="progressbar"
                        style={{
                          width: `${
                            getCompletionPercentage("exterior") * 0.25
                          }%`,
                        }}
                      ></div>
                      <div
                        className="progress-bar bg-info"
                        role="progressbar"
                        style={{
                          width: `${
                            getCompletionPercentage("interior") * 0.25
                          }%`,
                        }}
                      ></div>
                      <div
                        className="progress-bar bg-warning"
                        role="progressbar"
                        style={{
                          width: `${getCompletionPercentage("engine") * 0.25}%`,
                        }}
                      ></div>
                      <div
                        className="progress-bar bg-danger"
                        role="progressbar"
                        style={{
                          width: `${
                            getCompletionPercentage("cleaning") * 0.25
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t("notesAdded")}</span>
                      <span>{formData.notes ? "100%" : "0%"}</span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar ${
                          formData.notes ? "bg-success" : "bg-secondary"
                        }`}
                        role="progressbar"
                        style={{ width: formData.notes ? "100%" : "0%" }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span>{t("signatures")}</span>
                      <span>
                        {formData.technicianSignature ? "100%" : "0%"}
                      </span>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div
                        className={`progress-bar ${
                          formData.technicianSignature
                            ? "bg-success"
                            : "bg-secondary"
                        }`}
                        role="progressbar"
                        style={{
                          width: formData.technicianSignature ? "100%" : "0%",
                        }}
                      ></div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>

          {/* Right Panel - Content */}
          <Col lg={9}>
            <Card
              className={`shadow-sm ${
                mode === "dark" ? "bg-dark text-white border-dark" : ""
              }`}
            >
              <Card.Body>
                <Tab.Content>
                  {/* Excavator Selection Tab */}
                  <Tab.Pane active={activeTab === "excavator"}>
                    <h5 className="mb-4">{t("selectExcavator")}</h5>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-4">
                          <Form.Label>{t("excavator")}</Form.Label>
                          <Form.Select
                            name="excavatorId"
                            value={formData.excavatorId}
                            onChange={handleChange}
                            required
                            disabled={!!excavatorId}
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          >
                            <option value="">
                              {t("pleaseSelectExcavator")}
                            </option>
                            {excavators.map((excavator) => (
                              <option key={excavator._id} value={excavator._id}>
                                {excavator.manufacturer} {excavator.model} (
                                {excavator.serialNumber})
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <h5 className="mb-3">{t("excavatorInformation")}</h5>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("model")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="model"
                            value={formData.model}
                            onChange={handleChange}
                            placeholder="e.g., Volvo L30G"
                            readOnly
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("year")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="year"
                            value={formData.year}
                            onChange={handleChange}
                            placeholder="e.g., 2022"
                            readOnly
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("serialNumber")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="serialNumber"
                            value={formData.serialNumber}
                            onChange={handleChange}
                            placeholder="e.g., WLHZ1250LZK124142"
                            readOnly
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("machineType")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="machineType"
                            value={formData.machineType}
                            onChange={handleChange}
                            placeholder="e.g., Crawler, Wheeled"
                            readOnly
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("size")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            placeholder="e.g., Medium, Large"
                            readOnly
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("location")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="e.g., Site A"
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("service")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Service Information Tab */}
                  <Tab.Pane active={activeTab === "service"}>
                    <h5 className="mb-4">{t("serviceInformation")}</h5>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("serviceDate")}</Form.Label>
                          <Form.Control
                            type="date"
                            name="serviceDate"
                            value={formData.serviceDate}
                            onChange={handleChange}
                            required // Make the date input required
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("fuelLevel")} (%)</Form.Label>
                          <Form.Control
                            type="text"
                            name="fuelLevel"
                            value={formData.fuelLevel}
                            onChange={handleChange}
                            placeholder="e.g., 66%"
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("operatingHours")}</Form.Label>
                          <Form.Control
                            type="text"
                            name="operatingHours"
                            value={formData.operatingHours}
                            onChange={handleChange}
                            placeholder="e.g., 242"
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {t("nextMaintenance")} ({t("hours")})
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="nextMaintenance"
                            value={formData.nextMaintenance}
                            onChange={handleChange}
                            placeholder="e.g., 222"
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t("nextInspectionDate")}</Form.Label>
                          <Form.Control
                            type="date"
                            name="nextInspection"
                            value={formData.nextInspection}
                            onChange={handleChange}
                            className={
                              mode === "dark"
                                ? "bg-dark text-white border-secondary"
                                : ""
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("excavator")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("exterior")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Exterior Checks Tab */}
                  <Tab.Pane active={activeTab === "exterior"}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">{t("exteriorChecks")}</h5>
                      <Badge
                        bg={
                          getCompletionPercentage("exterior") === 100
                            ? "success"
                            : "primary"
                        }
                        className="fs-6 py-2 px-3"
                      >
                        {getCompletionPercentage("exterior")}% {t("completed")}
                      </Badge>
                    </div>

                    <Row className="gy-2">
                      {Object.keys(formData.exterior).map((key) => (
                        <Col md={4} key={key}>
                          <div
                            className={`rounded p-3 mb-2 ${
                              formData.exterior[key]
                                ? mode === "dark"
                                  ? "bg-success bg-opacity-25"
                                  : "bg-success-subtle"
                                : mode === "dark"
                                ? "bg-secondary bg-opacity-10"
                                : "bg-light"
                            }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`exterior-${key}`}
                              label={t(key)}
                              checked={formData.exterior[key]}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "exterior",
                                  key,
                                  e.target.checked
                                )
                              }
                              className="d-flex align-items-center gap-2 fs-6"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("service")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("interior")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Interior Checks Tab */}
                  <Tab.Pane active={activeTab === "interior"}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">{t("interiorChecks")}</h5>
                      <Badge
                        bg={
                          getCompletionPercentage("interior") === 100
                            ? "success"
                            : "primary"
                        }
                        className="fs-6 py-2 px-3"
                      >
                        {getCompletionPercentage("interior")}% {t("completed")}
                      </Badge>
                    </div>

                    <Row className="gy-2">
                      {Object.keys(formData.interior).map((key) => (
                        <Col md={4} key={key}>
                          <div
                            className={`rounded p-3 mb-2 ${
                              formData.interior[key]
                                ? mode === "dark"
                                  ? "bg-success bg-opacity-25"
                                  : "bg-success-subtle"
                                : mode === "dark"
                                ? "bg-secondary bg-opacity-10"
                                : "bg-light"
                            }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`interior-${key}`}
                              label={t(key)}
                              checked={formData.interior[key]}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "interior",
                                  key,
                                  e.target.checked
                                )
                              }
                              className="d-flex align-items-center gap-2 fs-6"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("exterior")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("engine")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Engine Checks Tab */}
                  <Tab.Pane active={activeTab === "engine"}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">{t("engineChecks")}</h5>
                      <Badge
                        bg={
                          getCompletionPercentage("engine") === 100
                            ? "success"
                            : "primary"
                        }
                        className="fs-6 py-2 px-3"
                      >
                        {getCompletionPercentage("engine")}% {t("completed")}
                      </Badge>
                    </div>

                    <Row className="gy-2">
                      {Object.keys(formData.engine).map((key) => (
                        <Col md={4} key={key}>
                          <div
                            className={`rounded p-3 mb-2 ${
                              formData.engine[key]
                                ? mode === "dark"
                                  ? "bg-success bg-opacity-25"
                                  : "bg-success-subtle"
                                : mode === "dark"
                                ? "bg-secondary bg-opacity-10"
                                : "bg-light"
                            }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`engine-${key}`}
                              label={t(key)}
                              checked={formData.engine[key]}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "engine",
                                  key,
                                  e.target.checked
                                )
                              }
                              className="d-flex align-items-center gap-2 fs-6"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("interior")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("cleaning")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Cleaning Tab */}
                  <Tab.Pane active={activeTab === "cleaning"}>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">{t("cleaning")}</h5>
                      <Badge
                        bg={
                          getCompletionPercentage("cleaning") === 100
                            ? "success"
                            : "primary"
                        }
                        className="fs-6 py-2 px-3"
                      >
                        {getCompletionPercentage("cleaning")}% {t("completed")}
                      </Badge>
                    </div>

                    <Row className="gy-2">
                      {Object.keys(formData.cleaning).map((key) => (
                        <Col md={4} key={key}>
                          <div
                            className={`rounded p-3 mb-2 ${
                              formData.cleaning[key]
                                ? mode === "dark"
                                  ? "bg-success bg-opacity-25"
                                  : "bg-success-subtle"
                                : mode === "dark"
                                ? "bg-secondary bg-opacity-10"
                                : "bg-light"
                            }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`cleaning-${key}`}
                              label={t(key)}
                              checked={formData.cleaning[key]}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "cleaning",
                                  key,
                                  e.target.checked
                                )
                              }
                              className="d-flex align-items-center gap-2 fs-6"
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("engine")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("notes")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Notes Tab */}
                  <Tab.Pane active={activeTab === "notes"}>
                    <h5 className="mb-4">{t("additionalNotes")}</h5>
                    <Form.Group className="mb-3">
                      <Form.Control
                        as="textarea"
                        rows={8}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder={t("additionalComments")}
                        className={
                          mode === "dark"
                            ? "bg-dark text-white border-secondary"
                            : ""
                        }
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("cleaning")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setActiveTab("signatures")}
                      >
                        {t("next")} <i className="bi bi-arrow-right ms-1"></i>
                      </Button>
                    </div>
                  </Tab.Pane>

                  {/* Signatures Tab */}
                  <Tab.Pane active={activeTab === "signatures"}>
                    <h5 className="mb-4">{t("signatures")}</h5>
                    <Row>
                      <Col md={formData.technicianSignature ? 6 : 12}>
                        <h6>{t("technicianSignature")}</h6>
                        <div
                          className={`signature-pad-container p-3 text-center border rounded mb-3 ${
                            mode === "dark" ? "bg-dark-subtle" : "bg-light"
                          }`}
                        >
                          {formData.technicianSignature ? (
                            <div>
                              <img
                                src={formData.technicianSignature}
                                alt={t("technicianSignature")}
                                className="img-fluid mb-2"
                                style={{ maxHeight: "100px" }}
                              />
                              <div>
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={clearTechSignature}
                                  className="mt-2"
                                >
                                  {t("clear")}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div
                                className={`signature-container border p-2 mb-3 ${
                                  mode === "dark" ? "bg-dark" : "bg-white"
                                }`}
                              >
                                <SignatureCanvas
                                  ref={techSignatureRef}
                                  canvasProps={{
                                    width: 500,
                                    height: 200,
                                    className: "signature-canvas",
                                    style: { width: "100%", height: "200px" },
                                  }}
                                  backgroundColor={
                                    mode === "dark" ? "#2d3748" : "#f8f9fa"
                                  }
                                />
                              </div>
                              <Button
                                variant="primary"
                                onClick={handleTechSignature}
                              >
                                {t("sign")} {formData.technician}
                              </Button>
                            </div>
                          )}
                        </div>
                      </Col>

                      {isManager() && (
                        <Col md={formData.technicianSignature ? 6 : 12}>
                          <Form.Group>
                            <Form.Label>{t("managerApproval")}</Form.Label>
                            <div
                              className={`signature-container border p-3 mb-2 ${
                                mode === "dark" ? "bg-dark-subtle" : "bg-light"
                              }`}
                            >
                              <SignatureCanvas
                                ref={managerSignatureRef}
                                canvasProps={{
                                  width: 500,
                                  height: 150,
                                  className: "signature-canvas",
                                }}
                                backgroundColor={
                                  mode === "dark" ? "#2d3748" : "#f8f9fa"
                                }
                              />
                            </div>
                            <div className="d-flex mb-3">
                              <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                onClick={handleManagerSignature}
                              >
                                {t("saveSignature")}
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={clearManagerSignature}
                              >
                                {t("clear")}
                              </Button>
                            </div>
                            {formData.managerSignature && (
                              <div className="mb-3">
                                <p className="text-success mb-1">
                                  ✓ {t("managerApproval")}
                                </p>
                                <img
                                  src={formData.managerSignature}
                                  alt={t("managerSignature")}
                                  className="img-fluid border"
                                  style={{ maxHeight: "100px" }}
                                />
                                <Form.Group className="mt-2">
                                  <Form.Control
                                    type="text"
                                    placeholder={t("managerName")}
                                    value={formData.reviewedBy}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        reviewedBy: e.target.value,
                                      })
                                    }
                                    required={!!formData.managerSignature}
                                    className={
                                      mode === "dark"
                                        ? "bg-dark text-white border-secondary"
                                        : ""
                                    }
                                  />
                                </Form.Group>
                              </div>
                            )}
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    <div className="d-flex justify-content-between mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setActiveTab("notes")}
                      >
                        <i className="bi bi-arrow-left me-1"></i> {t("back")}
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t("submitting")}
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-1"></i>{" "}
                            {t("submitReport")}
                          </>
                        )}
                      </Button>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default CreateReport;